/**
 * An editable diagram of one SysML view (issue #44).
 *
 * What is drawn is what the view EXPOSES; where it is drawn is the layout file
 * beside the model (best-decision-011). The two are saved separately and on
 * purpose: a MODEL edit - a state, a transition, a box added or removed - is
 * written at once, the way every other BEST edit is; moving and resizing boxes
 * is held until Save, because a commit per drag would bury the branch's history.
 *
 * A model edit reloads the view, since what the server regenerated is the only
 * truth about what the file now says. Unsaved box positions are CARRIED across
 * that reload, so adding a transition does not throw away an arrangement.
 *
 * Built on React Flow (best-decision-012). Positions are in `stateDiagram.ts` and
 * `arrange.ts`, edge shapes in `edgeGeometry.ts`, and every rule about what an
 * edit writes in `viewEdits.ts` and `transitionEdits.ts` - all tested apart from
 * anything that renders.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'
import '@xyflow/react/dist/style.css'
import { Background, Controls, MarkerType, ReactFlow, applyNodeChanges } from '@xyflow/react'
import type { Connection, EdgeChange, EdgeTypes, NodeChange, NodeTypes } from '@xyflow/react'
import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import { ApiError, api } from '../../api'
import type { EntityRow, LayoutBox, Transition, TransitionEdit, ViewDetail } from '../../schema'
import { useCanWrite } from '../../state/AppData'
import { useToasts } from '../../state/Toasts'
import DeleteElementDialog from '../tree/DeleteElementDialog'
import { arrange } from './arrange'
import NewStateDialog from './NewStateDialog'
import CreateStateDialog from '../states/CreateStateDialog'
import TransitionDialog from '../states/TransitionDialog'
import StateNodeView from './StateNodeView'
import TransitionEdgeView from './TransitionEdgeView'
import TransitionPanel from './TransitionPanel'
import { boxesFromNodes, mergeBoxes, placeMissing, stateNodes, transitionEdges } from './stateDiagram'
import type { StateNode, TransitionEdge } from './stateDiagram'
import { declaringState, relativeName, withAdded, withChanged, withRemoved } from './transitionEdits'
import type { StateRef, Verdict } from './transitionEdits'
import { addition, isRemoveKey, pairedRemoval, pickedAfter, removal, usableDefs, usageTarget } from './viewEdits'
import NewUsageDialog from './NewUsageDialog'
import GroupDialog from './GroupDialog'
import { isMachine } from '../states/stateViews'
import { FUNCTION_KIND, kindOf } from './diagramKinds'
import type { DiagramKind } from './diagramKinds'

const NODE_TYPES: NodeTypes = { state: StateNodeView }
const EDGE_TYPES: EdgeTypes = { transition: TransitionEdgeView }
// The background's dot spacing, and what a Shift-drag snaps to - one number,
// so a snapped box lands on the dots it is drawn over.
const GRID = 20

export default function ViewCanvas({ identifier }: { identifier: string }) {
  const theme = useTheme()
  const { allowed, reason } = useCanWrite()
  const toasts = useToasts()

  const [detail, setDetail] = useState<ViewDetail | null>(null)
  const [error, setError] = useState<ApiError | null>(null)
  const [nodes, setNodes] = useState<StateNode[]>([])
  const [dirty, setDirty] = useState(false)
  const [busy, setBusy] = useState(false)
  const [selectedNode, setSelectedNode] = useState<string | null>(null)
  const [selectedEdge, setSelectedEdge] = useState<string | null>(null)
  const [states, setStates] = useState<EntityRow[]>([])
  const [creating, setCreating] = useState(false)
  // An edge drawn between two states, waiting on its transition dialog.
  const [drawn, setDrawn] = useState<{ source: string; target: string } | null>(null)
  const [deleting, setDeleting] = useState(false)
  // Taking a usage off a paired diagram that would take more with it - its
  // transitions - and so is asked about first (decision 030).
  const [removingUsage, setRemovingUsage] = useState(false)
  // States waiting to be put into one machine: from an edge drawn between two
  // that share none (`drawn`), or from "Group into machine" on a selection.
  const [grouping, setGrouping] = useState<{ states: { id: string; name: string }[]; why?: string; drawn?: boolean } | null>(null)
  const [reloads, setReloads] = useState(0)
  // Shift held while dragging snaps to the background's grid. Read from the
  // window, not the canvas, so pressing it mid-drag works wherever the
  // pointer is. Pointer events carry it too: a Shift already down when the
  // window took focus sends no keydown here, and every drag move says whether
  // it is held. Cleared on blur, or a Shift released elsewhere sticks.
  const [snapping, setSnapping] = useState(false)
  useEffect(() => {
    const track = (event: globalThis.KeyboardEvent | PointerEvent) => setSnapping(event.shiftKey)
    const release = () => setSnapping(false)
    const events = ['keydown', 'keyup', 'pointerdown', 'pointermove'] as const
    for (const name of events) window.addEventListener(name, track, { capture: true, passive: true })
    window.addEventListener('blur', release)
    return () => {
      for (const name of events) window.removeEventListener(name, track, { capture: true })
      window.removeEventListener('blur', release)
    }
  }, [])

  // What a reload after a model edit must not throw away: the boxes as they
  // stand, and whether they were unsaved. A ref, so the load reads the values
  // at the moment of reloading rather than those the effect closed over.
  const carry = useRef<{ boxes: Record<string, LayoutBox>; dirty: boolean } | null>(null)
  const nodesRef = useRef(nodes)
  nodesRef.current = nodes
  const dirtyRef = useRef(dirty)
  dirtyRef.current = dirty

  const reload = useCallback((keepPositions = true) => {
    carry.current = keepPositions ? { boxes: boxesFromNodes(nodesRef.current), dirty: dirtyRef.current } : null
    setReloads((count) => count + 1)
  }, [])

  // What kind of diagram this is decides the nouns, the routes, and which
  // elements are boxes - everything else below is shared (diagramKinds.ts).
  const kind = useMemo(() => kindOf(detail ?? { type_name: '' }), [detail])
  const elements = useMemo(() => (detail ? kind.elements(detail) : []), [detail, kind])
  const labels = useMemo(() => ({ container: kind.containerLabel, leaf: kind.leafLabel, top: kind.topLabel }), [kind])

  const { edges: drawnEdges, hidden } = useMemo(() => transitionEdges(elements), [elements])
  const edges = useMemo(
    () => drawnEdges.map((edge) => ({ ...edge, selected: edge.id === selectedEdge })),
    [drawnEdges, selectedEdge],
  )

  useEffect(() => {
    let current = true
    const carried = carry.current
    carry.current = null
    setError(null)
    api
      .view(identifier)
      .then(async (found) => {
        // Unsaved positions first, then what the file holds. What neither
        // places is ARRANGED when nothing is placed yet, and otherwise put
        // beside what is - an arrangement would drop it on an existing box.
        // Either way, a box somebody placed never moves.
        const foundKind = kindOf(found)
        const boxes = foundKind.elements(found)
        const foundLabels = { container: foundKind.containerLabel, leaf: foundKind.leafLabel, top: foundKind.topLabel }
        const known = Object.fromEntries(
          Object.entries({ ...found.layout, ...(carried?.boxes ?? {}) })
            .filter(([name]) => boxes.some((box) => box.qualified_name === name)),
        )
        const { nodes: placed, missing } = stateNodes(boxes, known, foundLabels)
        let next = placed
        if (missing.length && Object.keys(known).length === 0) {
          next = stateNodes(
            boxes,
            mergeBoxes(known, await arrange(boxes, transitionEdges(boxes).edges)),
            foundLabels,
          ).nodes
        } else if (missing.length) {
          next = stateNodes(boxes, placeMissing(boxes, known), foundLabels).nodes
        }
        if (!current) return
        setDetail(found)
        setNodes(next)
        setDirty(missing.length > 0 || !!carried?.dirty)
        setSelectedNode(null)
        setSelectedEdge(null)
      })
      .catch((failure: ApiError) => {
        if (current) setError(failure)
      })
    return () => {
      current = false
    }
  }, [identifier, reloads])

  useEffect(() => {
    // Every element of the kind in the model: to offer what the view does not
    // show, and to know which enclosing names are machines or functions rather
    // than packages.
    if (!detail) return
    api
      .list(kind.plural, { limit: 5000 })
      .then((page) => setStates(page.items))
      .catch(() => setStates([]))
  }, [reloads, kind.plural, detail])

  const refs: StateRef[] = useMemo(
    () => states
      .filter((row) => row.qualified_name)
      .map((row) => ({ id: row.id, qualified_name: String(row.qualified_name) })),
    [states],
  )

  const onNodesChange = useCallback((changes: NodeChange<StateNode>[]) => {
    setNodes((now) => applyNodeChanges(changes, now))
    // Measuring fires 'dimensions' on first render; only a hand's resize or a
    // finished drag is an edit worth saving.
    if (changes.some((change) =>
      (change.type === 'position' && change.dragging === false) ||
      (change.type === 'dimensions' && change.resizing))) {
      setDirty(true)
    }
    for (const change of changes) {
      if (change.type === 'select') setSelectedNode((now) => pickedAfter(now, change.id, change.selected))
    }
  }, [])

  const onEdgesChange = useCallback((changes: EdgeChange<TransitionEdge>[]) => {
    for (const change of changes) {
      if (change.type === 'select') setSelectedEdge((now) => pickedAfter(now, change.id, change.selected))
    }
  }, [])

  const run = (work: () => Promise<unknown>) => {
    setBusy(true)
    work()
      .catch((failure: ApiError) => toasts.notify(failure.message, 'error'))
      .finally(() => setBusy(false))
  }

  /** Read the declaring element afresh, change its list, write it back, reload. */
  const writeTransitions = (owner: string, change: (fresh: Transition[]) => Verdict<TransitionEdit[]>) =>
    run(async () => {
      const fresh = await kind.readEdges(owner)
      const verdict = change(fresh)
      if (!verdict.allowed) {
        toasts.notify(verdict.reason, 'warning')
        return
      }
      await kind.writeEdges(owner, verdict.value)
      reload()
    })

  const onConnect = (connection: Connection) => {
    const verdict = declaringState(refs, connection.source, connection.target)
    if (!verdict.allowed) {
      // Two states that share no machine (decision 030, B): rather than a
      // dead end, offer to put both into one - as usages of their defs - and
      // then ask about the transition between the two usages.
      const ends = [connection.source, connection.target]
        .map((name) => elements.find((box) => box.qualified_name === name && !box.pseudo))
      if (kind !== FUNCTION_KIND && ends[0] && ends[1]) {
        setGrouping({
          states: ends.map((box) => ({ id: box!.id, name: box!.name })),
          why: verdict.reason,
          drawn: true,
        })
        return
      }
      toasts.notify(verdict.reason, 'warning')
      return
    }
    // A TRANSITION is asked about before it is written (issue #50): it
    // carries a trigger, a guard, an effect and who decides, and a bare edge
    // was the only thing drawing one could make. A succession between steps
    // carries none of those, so it is still written as drawn.
    if (kind !== FUNCTION_KIND) {
      setDrawn({ source: connection.source, target: connection.target })
      return
    }
    writeTransitions(verdict.value.id, (fresh) => ({
      allowed: true,
      value: withAdded(fresh, verdict.value, connection.source, connection.target),
    }))
  }

  const onReconnect = (old: TransitionEdge, connection: Connection) => {
    const data = old.data
    if (!data) return
    const verdict = declaringState(refs, connection.source, connection.target)
    if (!verdict.allowed || verdict.value.id !== data.owner) {
      toasts.notify(
        verdict.allowed
          ? `Both ends must stay inside ${data.owner}, which declares this transition. Delete it and draw a new one instead.`
          : verdict.reason,
        'warning',
      )
      return
    }
    writeTransitions(data.owner, (fresh) => withChanged(fresh, data.index, data.transition, {
      source: relativeName(verdict.value, connection.source),
      target: relativeName(verdict.value, connection.target),
    }))
  }

  const arrangeAll = () =>
    run(async () => {
      if (!detail) return
      setNodes(stateNodes(elements, await arrange(elements, drawnEdges), labels).nodes)
      setDirty(true)
    })

  const save = () =>
    run(async () => {
      const result = await api.setLayout(identifier, boxesFromNodes(nodes))
      setDirty(false)
      toasts.notify(result.changed ? `Layout saved to ${result.path}` : 'Layout unchanged', 'success')
    })

  // The view as viewEdits reads it: its exposes, and the boxes of THIS kind.
  const shape = detail ? { ...detail, states: elements } : null
  // The one machine a state view is PAIRED with (decision 030), or null. In a
  // paired view a state is put on the diagram by declaring a usage of its def
  // in the machine, and taken off by removing that usage; the exposes stay
  // `M::**`. An unpaired view is edited through its exposes, as before.
  const machine = kind !== FUNCTION_KIND ? detail?.machine ?? null : null
  const target = shape ? usageTarget(shape, selectedNode) : null

  const removeSelected = () =>
    run(async () => {
      if (!shape || !selectedNode) return
      if (machine) {
        const box = elements.find((state) => state.qualified_name === selectedNode)
        if (!box) return
        // Asked first when anything goes with it; said after when nothing did.
        const impact = await api.deleteImpact(kind.plural, box.id)
        if (impact.removes.length > 1 || impact.unlinks.length > 0) {
          setRemovingUsage(true)
          return
        }
        await api.remove(kind.plural, box.id)
        toasts.succeeded(`${box.name} removed from ${machine.name}; its def stays`)
        reload()
        return
      }
      await api.setExposes(identifier, removal(shape, selectedNode).exposes)
      reload()
    })

  const add = (row: EntityRow | null) =>
    run(async () => {
      if (row && machine && target) {
        const made = await api.createStateUsage({ machine: target, definition: row.id })
        toasts.succeeded(`${made.name} added to ${target.split('::').pop()}`)
        reload()
        return
      }
      const name = row ? String(row.qualified_name ?? '') : ''
      const exposes = shape && name ? addition(shape, name) : null
      if (!exposes) return
      await api.setExposes(identifier, exposes)
      reload()
    })

  if (error) return <Alert severity="error">{error.message}</Alert>
  if (!detail) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    )
  }

  const shown = new Set(elements.map((state) => state.qualified_name))
  // A paired view offers every def: the same def may be used more than once,
  // and the def itself is never on the diagram - only usages of it are.
  const addable = machine && shape
    ? usableDefs(shape, states)
    : kind.addable(states).filter((row) => row.qualified_name && !shown.has(String(row.qualified_name)))
  const pickedBox = elements.find((state) => state.qualified_name === selectedNode)
  // A `start` or `done` mark is part of its function's flow, not an element:
  // there is nothing to stop showing and nothing to delete.
  const pickedState = pickedBox?.pseudo ? undefined : pickedBox
  const verdict = !selectedNode || !shape ? null
    : pickedBox?.pseudo
      ? { allowed: false, exposes: [], reason: `${pickedBox.name} is part of its function’s flow, not an element of the model.` }
      : pairedRemoval(shape, selectedNode)
  const why = (control: string) => (allowed ? control : reason)
  // The Delete key is the "Remove from diagram" button, under the same rules:
  // where the button would be disabled, the key says why instead.
  const onCanvasKeyDown = (event: KeyboardEvent) => {
    if (!selectedNode || !isRemoveKey(event.key, event.target)) return
    event.preventDefault()
    if (busy) return
    if (!allowed) toasts.notify(reason, 'warning')
    else if (!verdict?.allowed) toasts.notify(verdict?.reason ?? 'Select a box first', 'warning')
    else removeSelected()
  }
  const pickedEdge = drawnEdges.find((edge) => edge.id === selectedEdge)
  // A new box goes inside the selected one, or beside the view in its package.
  const viewPackage = String(detail.qualified_name ?? '').split('::').slice(0, -1).join('::')
  const newStateInside = pickedState?.qualified_name ?? viewPackage
  // Only a state DEFINITION holds new state defs (OWNING_KEYWORDS), so a
  // selected usage is not somewhere to put one - said here, not by a refusal
  // after the form. In a paired view what is made is a USAGE, and a usage may
  // hold usages, so there it is no reason.
  const insideUsage = !machine && kind !== FUNCTION_KIND && pickedState?.is_definition === false
  const targetLabel = pickedState?.name ?? machine?.name ?? ''
  // Every selected box, for "Group into machine" (decision 030, C) - React Flow
  // keeps the selection on the nodes; `selectedNode` is only the last one
  // picked. Never the view's own machine, which cannot go inside itself.
  const groupable = nodes
    .filter((node) => node.selected)
    .map((node) => elements.find((box) => box.qualified_name === node.id))
    .filter((box): box is NonNullable<typeof box> =>
      Boolean(box) && !box?.pseudo && box?.qualified_name !== machine?.qualified_name)
  // The boxes as the transition dialog reads states: by id, with their names.
  const stateRows = elements.filter((box) => !box.pseudo).map((box) => ({
    kind: 'state', id: box.id, name: box.name, qualified_name: box.qualified_name, line: 0,
  }))
  const drawnSource = drawn && stateRows.find((row) => row.qualified_name === drawn.source)
  const drawnTarget = drawn && stateRows.find((row) => row.qualified_name === drawn.target)
  const edgePlural = `${kind.edgeNoun}s`

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
        <Tooltip title={why('Place every box afresh. Nothing is saved until you save.')}>
          <span>
            <Button size="small" onClick={arrangeAll} disabled={!allowed || busy}>
              Arrange all
            </Button>
          </span>
        </Tooltip>
        <Tooltip title={why(dirty ? 'Commit where the boxes are to the layout file' : 'Nothing has moved')}>
          <span>
            <Button size="small" variant="contained" onClick={save} disabled={!allowed || busy || !dirty}>
              Save layout
            </Button>
          </span>
        </Tooltip>
        <Button size="small" onClick={() => reload(false)} disabled={busy || !dirty}>
          Discard
        </Button>
        <Tooltip title={why(insideUsage
          ? `${pickedState?.name} is a state usage, and only a state definition holds states`
          : machine
            ? `Make a state def beside ${machine.name} and put a usage of it in ${targetLabel}`
            : `Declare a ${kind.noun} in ${newStateInside || 'the model'}`)}>
          <span>
            <Button size="small" onClick={() => setCreating(true)}
              disabled={!allowed || busy || !newStateInside || insideUsage}>
              New {kind.noun}
            </Button>
          </span>
        </Tooltip>
        <Tooltip
          title={
            !allowed ? reason : !verdict ? 'Select a box first' : !verdict.allowed
              ? verdict.reason
              : machine
                ? `Take it out of ${machine.name}: the usage goes, with its transitions. Its def stays.`
                : 'Stop showing it here. The element stays in the model.'
          }
        >
          <span>
            <Button
              size="small"
              color="warning"
              onClick={removeSelected}
              disabled={!allowed || busy || !verdict?.allowed}
            >
              Remove from diagram
            </Button>
          </span>
        </Tooltip>
        <Tooltip title={why(pickedState ? `Delete ${pickedState.id} from the model` : 'Select a box first')}>
          <span>
            <Button
              size="small"
              color="error"
              onClick={() => setDeleting(true)}
              disabled={!allowed || busy || !pickedState}
            >
              Delete from model
            </Button>
          </span>
        </Tooltip>
        {kind !== FUNCTION_KIND && (
          <Tooltip title={why(groupable.length
            ? `Put ${groupable.map((box) => box.name).join(', ')} into a machine, as usages of their defs`
            : 'Select one or more boxes first: a Shift-drag on the empty canvas selects several')}>
            <span>
              <Button
                size="small"
                onClick={() => setGrouping({ states: groupable.map((box) => ({ id: box.id, name: box.name })) })}
                disabled={!allowed || busy || !groupable.length}
              >
                Group into machine
              </Button>
            </span>
          </Tooltip>
        )}
        <Autocomplete
          size="small"
          sx={{ minWidth: 260 }}
          options={addable}
          disabled={!allowed || busy}
          getOptionLabel={(row) => `${row.id} — ${String(row.qualified_name ?? '')}`}
          onChange={(_event, row) => add(row)}
          value={null}
          renderInput={(params) => (
            <TextField
              {...params}
              label={!allowed ? reason
                : machine ? `Add a state to ${targetLabel}`
                  : `Add a ${kind === FUNCTION_KIND ? 'function' : kind.noun}`}
            />
          )}
        />
        <Box sx={{ flexGrow: 1 }} />
        {busy && <CircularProgress size={18} />}
        {dirty && (
          <Typography variant="caption" color="warning.main">
            Unsaved layout
          </Typography>
        )}
      </Stack>

      {allowed && (
        <Typography variant="caption" color="text.secondary">
          Drag from a {kind.noun}’s right-hand dot to another to draw a {kind.edgeNoun}; drag an
          arrow’s end to reconnect it. Select an arrow to {kind.edgeFields.length ? 'edit' : 'delete'} it.
        </Typography>
      )}

      {hidden > 0 && (
        // Said, not silently dropped: the diagram is not the whole flow.
        <Alert severity="info">
          {hidden} {hidden === 1 ? kind.edgeNoun : edgePlural} not drawn: an end is not in this view.
        </Alert>
      )}

      {pickedEdge?.data && (
        <TransitionPanel
          // Keyed by the edge and its content, so a reload after a save resets it.
          key={`${pickedEdge.id}:${JSON.stringify(pickedEdge.data.transition)}`}
          transition={pickedEdge.data.transition}
          owner={pickedEdge.data.owner}
          noun={kind.edgeNoun}
          fields={kind.edgeFields}
          decider={kind !== FUNCTION_KIND}
          disabled={allowed ? (busy ? 'Saving…' : '') : reason}
          onSave={(patch) => {
            const data = pickedEdge.data as NonNullable<TransitionEdge['data']>
            writeTransitions(data.owner, (fresh) => withChanged(fresh, data.index, data.transition, patch))
          }}
          onDelete={() => {
            const data = pickedEdge.data as NonNullable<TransitionEdge['data']>
            writeTransitions(data.owner, (fresh) => withRemoved(fresh, data.index, data.transition))
          }}
        />
      )}

      <Box
        sx={{ height: 560, border: 1, borderColor: 'divider', borderRadius: 1 }}
        data-testid="view-canvas"
        onKeyDown={onCanvasKeyDown}
      >
        {elements.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 4, textAlign: 'center' }}>
            This view shows no {kind === FUNCTION_KIND ? 'functions' : `${kind.noun}s`} yet. Add one above.
          </Typography>
        ) : (
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={NODE_TYPES}
            edgeTypes={EDGE_TYPES}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onReconnect={onReconnect}
            nodesDraggable={allowed}
            nodesConnectable={allowed}
            edgesReconnectable={allowed}
            elementsSelectable
            // React Flow's own delete would remove boxes from the canvas and
            // nothing from the view - an edit that looks done and is not. The
            // Delete key is handled on the wrapper instead (onCanvasKeyDown).
            deleteKeyCode={null}
            snapToGrid={snapping}
            snapGrid={[GRID, GRID]}
            colorMode={theme.palette.mode}
            defaultEdgeOptions={{ markerEnd: { type: MarkerType.ArrowClosed } }}
            fitView
            proOptions={{ hideAttribution: true }}
          >
            <Background gap={GRID} />
            <Controls showInteractive={false} />
          </ReactFlow>
        )}
      </Box>

      {drawnSource && drawnTarget && (
        <TransitionDialog
          state={drawnSource}
          states={stateRows}
          other={drawnTarget.id}
          direction="to"
          onClose={() => setDrawn(null)}
          onSaved={() => {
            setDrawn(null)
            reload()
          }}
        />
      )}

      {creating && machine && target && (
        <NewUsageDialog
          machine={target}
          label={targetLabel}
          onClose={() => setCreating(false)}
          onCreated={(made) => {
            setCreating(false)
            toasts.succeeded(`${made.definition} created, and ${made.name} added to ${targetLabel}`)
            reload()
          }}
        />
      )}

      {grouping && (
        <GroupDialog
          states={grouping.states}
          view={identifier}
          machines={states.filter(isMachine)}
          preferred={machine?.id}
          why={grouping.why}
          onClose={() => setGrouping(null)}
          onGrouped={(result) => {
            const wasDrawn = grouping.drawn
            setGrouping(null)
            toasts.succeeded(`Put into ${result.machine.split('::').pop()}; this view now shows it`)
            reload()
            // The edge that was drawn, between the two usages now: asked about
            // as any drawn transition is, once the reload shows them.
            if (wasDrawn && result.usages.length === 2) {
              setDrawn({ source: result.usages[0].qualified_name, target: result.usages[1].qualified_name })
            }
          }}
        />
      )}

      {removingUsage && machine && pickedState && (
        <DeleteElementDialog
          row={{ plural: kind.plural, id: pickedState.id, name: pickedState.name }}
          title={`Remove ${pickedState.name} from ${machine.name}?`}
          onClose={() => setRemovingUsage(false)}
          onDeleted={(message) => {
            setRemovingUsage(false)
            toasts.succeeded(`${message}; its def stays`)
            reload()
          }}
        />
      )}

      {creating && !machine && (
        <NewElementDialog
          kind={kind}
          inside={newStateInside}
          label={pickedState?.name ?? newStateInside}
          onClose={() => setCreating(false)}
          onCreated={(created) =>
            run(async () => {
              setCreating(false)
              toasts.succeeded(`${created} created`)
              // Shown already if an expose covers where it went; if not, the
              // view is told to show it, since it was made from this diagram.
              // By the QUALIFIED NAME the server made - built from the name,
              // not the ID, so it cannot be put together here.
              const fresh = await api.view(identifier)
              if (!kindOf(fresh).elements(fresh).some((state) => state.id === created)) {
                const row = await api.get(kind.plural, created)
                const exposes = addition({ ...fresh, states: kindOf(fresh).elements(fresh) }, String(row.qualified_name ?? ''))
                if (exposes) await api.setExposes(identifier, exposes)
              }
              reload()
            })}
        />
      )}

      {deleting && pickedState && (
        <DeleteElementDialog
          row={{ plural: kind.plural, id: pickedState.id, name: pickedState.name }}
          onClose={() => setDeleting(false)}
          onDeleted={(message) => {
            setDeleting(false)
            toasts.succeeded(message)
            reload()
          }}
        />
      )}
    </Stack>
  )
}

/**
 * The dialog a new box on this diagram is made with: the state wizard for a
 * state (issue #50), and the step dialog - which asks what the step performs -
 * for a function's step.
 */
function NewElementDialog({
  kind,
  inside,
  label,
  onClose,
  onCreated,
}: {
  kind: DiagramKind
  inside: string
  label: string
  onClose: () => void
  onCreated: (identifier: string) => void
}) {
  if (kind !== FUNCTION_KIND) {
    return <CreateStateDialog inside={{ qualifiedName: inside, label }} onClose={onClose} onCreated={onCreated} />
  }
  return (
    <NewStateDialog
      inside={inside}
      noun={kind.noun}
      plural={kind.plural}
      typed={kind.newIsTyped}
      onClose={onClose}
      onCreated={onCreated}
    />
  )
}
